'use client';

import { createDeepResearchSnapshot } from '@/app/services/deep-research';
import { Button, ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import copy from 'copy-to-clipboard';
import React, { useState } from 'react';
import { toast } from 'sonner';

type SharedSnapshotPayload = {
  snapshotId: string;
  shareToken: string;
  shareUrl: string;
};

export default function ShareButton({
  disabled,
  sid,
  className,
  variant,
  size,
  children,
  onShared,
}: {
  disabled: boolean;
  sid: string;
  className?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  children?: React.ReactNode;
  onShared?: (payload: SharedSnapshotPayload) => void;
}) {
  const [loading, setLoading] = useState(false);

  const handleShare = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (loading || disabled) {
      return;
    }

    try {
      setLoading(true);
      const { data } = await createDeepResearchSnapshot(sid || '');
      const shareUrl =
        typeof window !== 'undefined'
          ? new URL(data.share_url, window.location.origin).toString()
          : data.share_url;

      copy(shareUrl);
      onShared?.({
        snapshotId: data.snapshot_id,
        shareToken: data.share_token,
        shareUrl: data.share_url,
      });
      toast.success('链接已复制到剪贴板，任何拥有该链接的人都可以查看', {
        position: 'top-center',
      });
    } catch (error) {
      console.error(error);
      toast.error('分享失败，请稍后重试', {
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      className={cn(className)}
      size={size || 'sm'}
      variant={variant || 'outline-solid'}
      disabled={disabled || loading}
      onClick={handleShare}
      color="primary"
    >
      {children}
    </Button>
  );
}
